import { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Gavel, FileWarning, Sparkles, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/dateFormat";

/**
 * AdverseActionDialog — FCRA-compliant Adverse Action notice generator.
 *
 * Surfaced from the DecisionHero card whenever the recommendation is `deny`
 * or `conditional`. Auto-suggests adverse factors from DecisionPoint metrics,
 * lets the PM customize, and emails (with PDF attached) the applicant on submit.
 */
export default function AdverseActionDialog({ screening, onIssued }) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState({});
  const [decision, setDecision] = useState(screening.recommendation === "conditional" ? "conditional" : "deny");
  const [custom, setCustom] = useState("");
  const [landlordAddress, setLandlordAddress] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  useEffect(() => {
    if (!open) return;
    apiClient.get(`/screenings/${screening.id}/adverse-action/suggested-factors`)
      .then(({ data }) => {
        setCatalog(data.catalog);
        const init = {};
        data.catalog.forEach((c) => { init[c.key] = data.suggested.includes(c.key); });
        setSelected(init);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load factors"));
  }, [open, screening.id]);

  // Pre-fill custom reasons from the staff recommendation reason if available.
  useEffect(() => {
    if (open && !custom && screening.recommendation_reason) setCustom(screening.recommendation_reason);
  }, [open, screening.recommendation_reason]); // eslint-disable-line react-hooks/exhaustive-deps

  const previousIssued = (screening.adverse_actions || []).slice().reverse();

  const issue = async () => {
    setBusy(true);
    try {
      const factor_keys = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
      const { data } = await apiClient.post(`/screenings/${screening.id}/adverse-action`, {
        decision, factor_keys,
        custom_reasons: custom || null,
        landlord_address: landlordAddress || null,
        send_email: sendEmail,
      });
      setIssued(data);
      toast.success(sendEmail && data.emailed_to ? "Notice issued and emailed to applicant" : "Notice generated");
      onIssued?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to issue notice");
    } finally {
      setBusy(false);
    }
  };

  const downloadNotice = async (id) => {
    try {
      const res = await apiClient.get(`/screenings/${screening.id}/adverse-action/${id}/pdf`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NTN-AdverseActionNotice-${screening.id.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setIssued(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-sm h-11 px-5 border-[#0A192F]/20 hover:border-[#0A192F]"
                data-testid="open-adverse-action">
          <FileWarning className="h-4 w-4 mr-2" /> Generate Adverse Action notice
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Gavel className="h-5 w-5" /> FCRA Adverse Action Notice
          </DialogTitle>
          <DialogDescription>
            Required by 15 U.S.C. § 1681m when a rental decision is based, in whole or part,
            on a consumer report. We'll generate a compliant PDF and email it to the applicant.
          </DialogDescription>
        </DialogHeader>

        {issued ? (
          <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-sm p-5 space-y-3" data-testid="aa-issued">
            <div className="font-semibold text-[#15803D]">Notice issued · {fmtDateTime(issued.issued_at)}</div>
            <div className="text-sm text-[#0A192F]">
              {issued.emailed_to
                ? <>Emailed to <strong>{issued.emailed_to}</strong> with the PDF attached.</>
                : <>PDF generated and stored. Email was not sent.</>}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => downloadNotice(issued.id)} className="bg-[#0A192F] rounded-sm" data-testid="aa-download">
                <Download className="h-4 w-4 mr-2" /> Download PDF
              </Button>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Decision picker */}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Decision</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {[
                  { v: "deny", label: "Denied" },
                  { v: "conditional", label: "Approved with conditions" },
                ].map((o) => (
                  <button key={o.v} type="button" onClick={() => setDecision(o.v)}
                    data-testid={`aa-decision-${o.v}`}
                    className={`px-3 py-2.5 rounded-sm border text-sm text-left transition-colors ${
                      decision === o.v
                        ? "border-[#0A192F] bg-[#0A192F] text-white"
                        : "border-[#0A192F]/20 hover:border-[#0A192F]/40 text-[#0A192F]"
                    }`}>{o.label}</button>
                ))}
              </div>
            </div>

            {/* Factor checklist */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
                  Principal reasons (select all that apply)
                </Label>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.12em] text-[#4A5568]">
                  <Sparkles className="h-3 w-3" /> AUTO-SUGGESTED FROM DECISIONPOINT
                </span>
              </div>
              <div className="mt-2 space-y-2 border border-[#0A192F]/10 rounded-sm p-3 bg-[#F3EFEA]/30">
                {catalog.map((c) => (
                  <label key={c.key} className="flex items-start gap-2.5 cursor-pointer text-sm text-[#0A192F]"
                         data-testid={`aa-factor-${c.key}`}>
                    <Checkbox checked={!!selected[c.key]}
                              onCheckedChange={(v) => setSelected((s) => ({ ...s, [c.key]: !!v }))} />
                    <span>{c.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
                Additional notes (optional)
              </Label>
              <Textarea rows={3} value={custom} onChange={(e) => setCustom(e.target.value)}
                placeholder="Free-text reasons or conditions communicated to the applicant"
                className="mt-1.5 rounded-sm" data-testid="aa-custom-reasons" />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
                Your mailing address (optional, appears on notice)
              </Label>
              <Input value={landlordAddress} onChange={(e) => setLandlordAddress(e.target.value)}
                placeholder="123 Main St, San Francisco, CA 94102"
                className="mt-1.5 h-11 rounded-sm" data-testid="aa-landlord-address" />
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer text-sm text-[#0A192F]" data-testid="aa-send-email-toggle">
              <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(!!v)} />
              <span className="leading-snug">
                <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Email PDF to applicant</span>
                <span className="block text-xs text-[#4A5568] mt-0.5">
                  Sends to <strong>{screening.applicant_email}</strong> with the FCRA notice attached.
                </span>
              </span>
            </label>

            {previousIssued.length > 0 && (
              <div className="border-t border-[#0A192F]/10 pt-3">
                <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
                  Previously issued
                </Label>
                <ul className="mt-2 space-y-1.5">
                  {previousIssued.map((n) => (
                    <li key={n.id} className="flex items-center justify-between text-xs text-[#4A5568]">
                      <span>
                        {fmtDateTime(n.issued_at)} · <strong className="text-[#0A192F]">{n.decision === "deny" ? "DENIED" : "CONDITIONAL"}</strong>
                        {n.emailed_to && <span> · emailed</span>}
                      </span>
                      <button onClick={() => downloadNotice(n.id)} className="text-[#0A192F] underline" data-testid={`aa-prev-${n.id}`}>
                        Download
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {!issued && (
          <DialogFooter>
            <Button onClick={issue} disabled={busy} className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-11 px-5"
                    data-testid="aa-issue-btn">
              {busy ? "Generating…" : (sendEmail ? "Generate & send notice" : "Generate notice")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
